import {
  CHAT_STATUS_FILTER,
  FEATURES,
  GROUP_TYPE,
  IDepartmentData,
  PATIENT_STATUS,
  SERVICE_STATUS,
} from "@home-health-notify/domain";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import {
  getGroupStatusProperty,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import React, { useContext } from "react";
import {
  ColumnLayoutItem,
  TwoColumnLayoutWrapper,
} from "../common/Common.style";
import PatientHomeIcon from "../common/PatientHomeIcon";
import { FilterDataType } from "./ChatListPanel";

const GroupListFilter = ({
  filterData,
  onSetFilterData,
  groupType,
}: {
  filterData: FilterDataType;
  onSetFilterData: (key: string, value: any) => void;
  groupType: GROUP_TYPE;
}) => {
  const { t } = useTranslation();
  const { selectedOrgUser } = useContext(OrganizationContext);
  const canFilterByServiceStatus = !!selectedOrgUser?.hasAccess(
    FEATURES.FILTER_BY_SERVICE_STATUS,
  );
  const canSortByPendingReferrals = !!selectedOrgUser?.hasAccess(
    FEATURES.SORT_BY_PENDING_REFERRALS,
  );
  const departments: IDepartmentData[] =
    selectedOrgUser?.getDepartments() || [];
  const setServiceToPatient = (key: "id" | "status", value: string) => {
    let serviceToPatient = { ...filterData.servicesToPatient };
    serviceToPatient[key] = value as SERVICE_STATUS;
    onSetFilterData("servicesToPatient", serviceToPatient);
  };

  const onPatientStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let clonedPatientStatus: number[] = [...(filterData.patientStatus ?? [])];
    const value = +e.target.value;
    if (e.target.checked) {
      clonedPatientStatus.push(value);
    } else {
      clonedPatientStatus = clonedPatientStatus.filter(data => data !== value);
    }
    onSetFilterData("patientStatus", clonedPatientStatus);
  };

  return (
    <>
      {(groupType === GROUP_TYPE.PATIENT ||
        groupType === GROUP_TYPE.HIDDEN) && (
        <Accordion
          defaultExpanded={true}
          elevation={3}
          sx={{ borderRadius: "4px" }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="accordion-content"
          >
            <Typography>{t("filter.patientStatus")}</Typography>
          </AccordionSummary>
          <AccordionDetails data-testid="filter-box">
            <TwoColumnLayoutWrapper>
              {Object.values(PATIENT_STATUS).map(function (key) {
                let patientStatus: number = +key;
                let patientStatusTitle =
                  getGroupStatusProperty(patientStatus).title;
                return (
                  <ColumnLayoutItem key={patientStatusTitle}>
                    <Checkbox
                      value={patientStatus}
                      data-testid={patientStatusTitle}
                      checked={
                        !!filterData.patientStatus?.includes(patientStatus)
                      }
                      onChange={onPatientStatusChange}
                    />
                    <div style={{ marginRight: 10 }}>
                      <PatientHomeIcon size={30} status={patientStatus} />
                    </div>
                    <Typography>{patientStatusTitle}</Typography>
                  </ColumnLayoutItem>
                );
              })}
            </TwoColumnLayoutWrapper>
          </AccordionDetails>
        </Accordion>
      )}
      {canFilterByServiceStatus &&
        (groupType === GROUP_TYPE.PATIENT ||
          groupType === GROUP_TYPE.HIDDEN) && (
        <Accordion
          defaultExpanded={true}
          elevation={3}
          sx={{ borderRadius: "4px" }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="accordion-content"
          >
            <Typography>{t("filter.serviceStatus")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <>
              <RadioGroup
                row
                name="serviceRow"
                value={filterData.servicesToPatient?.id}
                sx={{ marginLeft: "10px" }}
                onChange={e => setServiceToPatient("id", e.target.value)}
              >
                {departments.map((department: IDepartmentData) => {
                  return (
                    <FormControlLabel
                      key={department.id}
                      value={department.id}
                      control={<Radio />}
                      label={department.shortHand}
                      sx={{ minWidth: "80px", marginRight: "36px" }}
                    />
                  );
                })}
              </RadioGroup>
              <RadioGroup
                row
                name="statusRow"
                value={filterData.servicesToPatient?.status}
                sx={{ margin: "14px 0 10px 10px" }}
                onChange={e => setServiceToPatient("status", e.target.value)}
              >
                <FormControlLabel
                  value={SERVICE_STATUS.PENDING}
                  control={<Radio />}
                  label="Pending"
                  sx={{ minWidth: "80px", marginRight: "24px" }}
                />
                <FormControlLabel
                  value={SERVICE_STATUS.ACTIVE}
                  control={<Radio />}
                  label="Active"
                  sx={{ minWidth: "80px", marginRight: "24px" }}
                />
                <FormControlLabel
                  value={SERVICE_STATUS.DISCHARGED}
                  control={<Radio />}
                  label="Discharged"
                  sx={{ minWidth: "80px", marginRight: "24px" }}
                />
              </RadioGroup>
            </>
          </AccordionDetails>
        </Accordion>
      )}
      <Accordion
        defaultExpanded={true}
        elevation={3}
        sx={{ borderRadius: "4px" }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="accordion-content"
        >
          <Typography>{t("filter.chatsBy")}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <RadioGroup
            row
            name="chatStatusRow"
            value={filterData.filterBy}
            sx={{ margin: "0 0 10px 10px" }}
            onChange={e => onSetFilterData("filterBy", e.target.value)}
          >
            <FormControlLabel
              value={CHAT_STATUS_FILTER.DATE}
              control={<Radio />}
              label="Date"
              sx={{ minWidth: "80px", marginRight: "24px" }}
            />
            <FormControlLabel
              value={CHAT_STATUS_FILTER.UNREAD_MESSAGES}
              control={<Radio />}
              label="Unread Messages"
              sx={{ minWidth: "80px", marginRight: "24px" }}
            />
            {canSortByPendingReferrals &&
              (groupType === GROUP_TYPE.PATIENT ||
                groupType === GROUP_TYPE.HIDDEN) && (
                <FormControlLabel
                  value={CHAT_STATUS_FILTER.PENDING_REFERRAL}
                  control={<Radio />}
                  label="Pending Referrals"
                  sx={{ minWidth: "80px", marginRight: "24px" }}
                />
              )}
          </RadioGroup>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default GroupListFilter;
